
import { useState, useEffect, useCallback } from "react";
import { EmailRecord, EmailStats } from "@/types/email";
import { supabase } from "@/integrations/supabase/client"; 
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useGoogleAuth } from "@/contexts/GoogleAuthContext";

/**
 * Hook for handling email storage operations with Supabase
 */
export const useEmailStorage = () => {
  const { supabaseSession } = useGoogleAuth();
  const [cachedEmails, setCachedEmails] = useState<EmailRecord[] | null>(null);
  const [cachedDrafts, setCachedDrafts] = useState<EmailRecord[] | null>(null);
  
  // Load emails from Supabase with caching
  const loadEmailData = useCallback(async () => {
    // Return cached data if available
    if (cachedEmails !== null) {
      return cachedEmails;
    }
    
    // Load from Supabase
    if (supabaseSession) {
      const userId = supabaseSession.user.id;
      
      try {
        // Get email templates (drafts)
        const { data: templates } = await supabase
          .from('email_templates')
          .select('*')
          .eq('user_id', userId);
          
        // Get sent emails
        const { data: sentEmails } = await supabase
          .from('sent_emails')
          .select('*')
          .eq('user_id', userId);
          
        if ((templates && templates.length > 0) || (sentEmails && sentEmails.length > 0)) {
          // Convert Supabase data to EmailRecord format
          const emailRecords: EmailRecord[] = [
            ...(templates || []).map((template): EmailRecord => ({
              id: template.id,
              investorId: template.investor_id,
              contactId: template.contact_id,
              subject: template.subject || "",
              body: template.content,
              sentAt: "",
              sentVia: null,
              hasResponse: false,
              status: "draft",
              createdAt: new Date(template.created_at),
              label: template.label || "Draft"
            })),
            ...(sentEmails || []).map((email): EmailRecord => ({
              id: email.id,
              investorId: email.investor_id,
              contactId: email.contact_id,
              subject: email.subject || "",
              body: email.content,
              sentAt: email.sent_at,
              sentVia: (email.sent_via as "gmail" | "outlook" | "manual" | null) || "manual",
              hasResponse: false,
              status: "sent",
              createdAt: new Date(email.sent_at),
              label: "Sent Email"
            }))
          ];
          
          console.log(`Loaded ${emailRecords.length} emails from Supabase`);
          setCachedEmails(emailRecords);
          return emailRecords;
        } else {
          console.log("No email data found in Supabase");
          setCachedEmails([]);
          return [];
        }
      } catch (error) {
        console.error("Error loading emails from Supabase:", error);
        setCachedEmails([]);
        return [];
      }
    }
    
    // No authenticated session
    console.log("No authenticated session, returning empty emails array");
    setCachedEmails([]);
    return [];
  }, [supabaseSession, cachedEmails]);

  // Load only drafts from storage
  const loadDraftsFromStorage = useCallback(async () => {
    // Return cached drafts if available
    if (cachedDrafts !== null) {
      return cachedDrafts;
    }

    // Get all emails first
    const allEmails = await loadEmailData();
    
    // Filter for drafts only
    const drafts = allEmails.filter(email => email.status === "draft");
    
    setCachedDrafts(drafts);
    return drafts;
  }, [loadEmailData, cachedDrafts]);

  // Save email and sync with Supabase
  const saveEmailRecord = async (emailRecord: EmailRecord) => {
    try {
      // Get current emails from cache or load them
      const currentEmails = cachedEmails || await loadEmailData();
      const updatedEmails = [...currentEmails, emailRecord];
      
      // Sync with Supabase if we have a user
      if (supabaseSession) {
        const userId = supabaseSession.user.id;
        
        if (emailRecord.status === "sent") {
          await supabase.from('sent_emails').insert([{
            id: emailRecord.id,
            user_id: userId,
            investor_id: emailRecord.investorId,
            contact_id: emailRecord.contactId,
            subject: emailRecord.subject,
            content: emailRecord.body,
            sent_at: emailRecord.sentAt,
            sent_via: emailRecord.sentVia || "manual"
          }]);
        } else {
          await supabase.from('email_templates').insert([{
            id: emailRecord.id,
            user_id: userId,
            investor_id: emailRecord.investorId,
            contact_id: emailRecord.contactId,
            subject: emailRecord.subject,
            content: emailRecord.body,
            label: emailRecord.label
          }]);
        }
      } else {
        toast.error("You must be logged in to save emails");
        return currentEmails;
      }
      
      // Update the cache
      setCachedEmails(updatedEmails);
      
      // Update drafts cache if needed
      if (emailRecord.status === "draft") {
        const drafts = cachedDrafts ? [...cachedDrafts, emailRecord] : [emailRecord];
        setCachedDrafts(drafts);
      }
      
      return updatedEmails;
    } catch (error) {
      console.error("Error saving email:", error);
      throw error;
    }
  };

  // Update an existing email record
  const updateEmailRecord = async (updatedEmail: EmailRecord) => {
    try {
      // Get current emails from cache or load them
      const currentEmails = cachedEmails || await loadEmailData();
      
      const updatedEmails = currentEmails.map(email => 
        email.id === updatedEmail.id ? updatedEmail : email
      );
      
      // Sync with Supabase if we have a user
      if (supabaseSession) {
        const userId = supabaseSession.user.id;
        
        if (updatedEmail.status === "sent") {
          await supabase.from('sent_emails').update({
            subject: updatedEmail.subject,
            content: updatedEmail.body,
            sent_at: updatedEmail.sentAt,
            sent_via: updatedEmail.sentVia || "manual"
          }).eq('id', updatedEmail.id);
        } else {
          await supabase.from('email_templates').update({
            subject: updatedEmail.subject,
            content: updatedEmail.body,
            label: updatedEmail.label
          }).eq('id', updatedEmail.id);
        }
      } else {
        toast.error("You must be logged in to update emails");
        return currentEmails;
      }
      
      // Update the cache
      setCachedEmails(updatedEmails);
      
      // Update drafts cache if needed
      if (cachedDrafts !== null) {
        const updatedDrafts = cachedDrafts
          .filter(draft => draft.id !== updatedEmail.id || updatedEmail.status === "draft")
          .map(draft => draft.id === updatedEmail.id ? updatedEmail : draft);
        
        if (updatedEmail.status === "draft" && !updatedDrafts.some(d => d.id === updatedEmail.id)) {
          updatedDrafts.push(updatedEmail);
        }
        
        setCachedDrafts(updatedDrafts);
      }
      
      return updatedEmails;
    } catch (error) {
      console.error("Error updating email:", error);
      throw error;
    }
  };

  // Delete an email record
  const deleteEmailRecord = async (emailId: string) => {
    try {
      // Get current emails from cache or load them
      const currentEmails = cachedEmails || await loadEmailData();
      
      // Find the email to delete
      const emailToDelete = currentEmails.find(email => email.id === emailId);
      if (!emailToDelete) {
        throw new Error("Email not found");
      }
      
      // Filter out the email to delete
      const updatedEmails = currentEmails.filter(email => email.id !== emailId);
      
      // Sync with Supabase if we have a user
      if (supabaseSession) {
        const userId = supabaseSession.user.id;
        
        if (emailToDelete.status === "sent") {
          await supabase
            .from('sent_emails')
            .delete()
            .eq('id', emailId);
        } else {
          await supabase
            .from('email_templates')
            .delete()
            .eq('id', emailId);
        }
      } else {
        toast.error("You must be logged in to delete emails");
        return currentEmails;
      }
      
      // Update the cache
      setCachedEmails(updatedEmails);
      
      // Update drafts cache if needed
      if (cachedDrafts !== null && emailToDelete.status === "draft") {
        const updatedDrafts = cachedDrafts.filter(draft => draft.id !== emailId);
        setCachedDrafts(updatedDrafts);
      }
      
      return updatedEmails;
    } catch (error) {
      console.error("Error deleting email:", error);
      throw error;
    }
  };

  // Mark a draft as sent
  const markDraftAsSent = async (emailRecord: EmailRecord) => {
    try {
      // Get current emails from cache or load them
      const currentEmails = cachedEmails || await loadEmailData();
      
      // Find the draft to update
      const draftIndex = currentEmails.findIndex(email => email.id === emailRecord.id);
      if (draftIndex === -1) {
        throw new Error("Draft not found");
      }
      
      // Replace the draft with the sent version
      const updatedEmails = [...currentEmails];
      updatedEmails[draftIndex] = emailRecord;
      
      // Sync with Supabase if we have a user
      if (supabaseSession) {
        const userId = supabaseSession.user.id;
        
        // Delete from email_templates
        await supabase
          .from('email_templates')
          .delete()
          .eq('id', emailRecord.id);
          
        // Insert into sent_emails
        await supabase.from('sent_emails').insert([{
          id: emailRecord.id,
          user_id: userId,
          investor_id: emailRecord.investorId,
          contact_id: emailRecord.contactId,
          subject: emailRecord.subject,
          content: emailRecord.body,
          sent_at: emailRecord.sentAt,
          sent_via: emailRecord.sentVia || "manual"
        }]);
      } else {
        toast.error("You must be logged in to send emails");
        return currentEmails;
      }
      
      // Update the cache
      setCachedEmails(updatedEmails);
      
      // Update drafts cache
      if (cachedDrafts !== null) {
        const updatedDrafts = cachedDrafts.filter(draft => draft.id !== emailRecord.id);
        setCachedDrafts(updatedDrafts);
      }
      
      return updatedEmails;
    } catch (error) {
      console.error("Error marking draft as sent:", error);
      throw error;
    }
  };

  // Make sure to clear cache when session changes
  useEffect(() => {
    setCachedEmails(null);
    setCachedDrafts(null);
  }, [supabaseSession]);

  return {
    loadEmailData,
    loadDraftsFromStorage,
    saveEmailRecord,
    updateEmailRecord,
    deleteEmailRecord,
    markDraftAsSent
  };
};
